import React, { useState } from 'react'
import { COLORS } from '../theme'
import { TH, TD, SortHeader } from './tableStyles.jsx'

// A table whose columns the PRODUCER declared.
//
// DevTable renders ONE table — epics by developer — and its headers are
// hard-coded to that shape. That was right while it was the only table on the
// board and wrong the moment a second metric wanted one: a source emitting
// different rows rendered under Points / Epics / Size-mix with every cell
// blank, which beside a person's name reads as "shipped nothing" rather than as
// a mismatched renderer. So a source that knows its own shape says so, and a
// source that declares nothing keeps the layout it shipped with.
//
// Column spec (the whole vocabulary — see CLAUDE.md, "The source contract"):
//   { key, label, align: 'left'|'right', sortable?: boolean, title?: string }

/** Rows a producer sent that we can actually render. */
export function usableColumns(columns) {
    if (!Array.isArray(columns)) return null
    const ok = columns.filter((c) => c && typeof c.key === 'string' && c.key.length > 0)
    // Collapsing to null rather than rendering a headerless table means a
    // malformed spec degrades to the ORIGINAL layout instead of taking the
    // board down. `#/dashboards` has no error boundary, so a throw inside this
    // render blanks every panel, not one tile — and the hub's whole contract is
    // that a bad feed degrades its own tile and says so.
    return ok.length ? ok : null
}

// A cell links only to an https URL the row carries in the field the column
// names. Anything else (a javascript: URL, a relative path) renders as text.
export function cellHref(row, column) {
    const v = column && column.link ? row?.[column.link] : null
    return typeof v === 'string' && /^https:\/\//.test(v) ? v : null
}

// `ordered`: the producer's row order is the reading order (newest first, a
// total row last), so nothing is sorted until a reader clicks a header.
export default function DeclaredTable({ rows, columns, note, ordered = false }) {
    const cols = usableColumns(columns)
    const initial = cols && !ordered ? (cols.find((c) => c.sortable) || cols[0]).key : null
    const [sortKey, setSortKey] = useState(initial)

    if (!rows || rows.length === 0 || !cols) return null

    // A spec that changed shape under a stale sort key would silently fall back
    // to source order while a header still rendered as the active sort — wrong
    // and confident, which is the failure this board's freshness contract
    // exists to prevent everywhere else.
    const active = cols.some((c) => c.key === sortKey) ? sortKey : initial
    const activeCol = cols.find((c) => c.key === active)

    const missing = (v) => v === null || v === undefined
    const sorted = active === null ? rows : [...rows].sort((a, b) => {
        const av = a[active]
        const bv = b[active]
        // NULLS LAST, always. A row whose value could not be measured must never
        // outrank one that was: sorting `String(null ?? '')` puts the empty
        // string before every digit, which floats the person we know least about
        // to the top of a table about people.
        if (missing(av) !== missing(bv)) return missing(av) ? 1 : -1
        if (missing(av)) return 0
        // Decided ONCE per column, not per pair. A per-pair typeof test gives
        // one comparator two orderings — numeric descending, string ascending —
        // which is non-transitive on mixed input, so the sort result is
        // arbitrary rather than merely wrong in one place.
        if (activeCol?.align === 'right') {
            const an = Number(av)
            const bn = Number(bv)
            // Postgres hands back counts as strings; coerce rather than trusting
            // the producer's JSON types, or "9" sorts above "12".
            if (Number.isFinite(an) && Number.isFinite(bn)) return bn - an
        }
        return String(av).localeCompare(String(bv))
    })

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                    <thead>
                        <tr>
                            {cols.map((c) => (
                                <th
                                    key={c.key}
                                    title={c.title || undefined}
                                    style={{ ...TH, textAlign: c.align === 'right' ? 'right' : 'left' }}
                                >
                                    {c.sortable ? (
                                        <SortHeader
                                            label={c.label}
                                            active={active === c.key}
                                            onClick={() => setSortKey(c.key)}
                                        />
                                    ) : (
                                        c.label
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r, i) => (
                            <tr key={r.username || r.developer || `row-${i}`}>
                                {cols.map((c) => (
                                    <td
                                        key={c.key}
                                        style={{
                                            ...TD,
                                            textAlign: c.align === 'right' ? 'right' : 'left',
                                            whiteSpace: c.wrap ? 'normal' : 'nowrap',
                                            fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : 'normal',
                                            fontWeight: c.align === 'right' ? 600 : 400,
                                        }}
                                    >
                                        {/* A cell with NO VALUE renders as an em dash. A real zero still
                                            renders as 0 — the two are different facts and the table exists
                                            to keep them apart. */}
                                        {missing(r[c.key]) ? '—' : cellHref(r, c) ? (
                                            <a href={cellHref(r, c)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary }}>
                                                {r[c.key]}
                                            </a>
                                        ) : r[c.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {note ? (
                <p
                    style={{
                        fontSize: 11.5,
                        lineHeight: 1.55,
                        color: COLORS.textMuted,
                        marginTop: 10,
                        paddingTop: 8,
                        borderTop: `1px dashed ${COLORS.border}`,
                    }}
                >
                    {note}
                </p>
            ) : null}
        </div>
    )
}
